import { useState, useCallback } from 'react';
import { supabase, requireOrganizationId } from '../lib/supabase';
import { useToast } from '../components/ui/ToastModal';
import { logError } from "../lib/logError";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface XeroIntegrationStatus {
  connected: boolean;
  tenant_name: string | null;
  config: {
    tenant_id?: string;
    tenant_name?: string;
    project_mappings?: Record<string, string>;
    account_mappings?: Record<string, string>;
  } | null;
  integration_id: string | null;
}

export interface XeroSyncResult {
  success: boolean;
  summary?: {
    projects_synced: number;
    total_rows_upserted: number;
    total_errors: number;
    months_range: string;
  };
  details?: {
    project: string;
    months_synced: number;
    rows_upserted: number;
    errors: string[];
  }[];
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useXeroIntegration() {
  const { addToast } = useToast();
  const [status, setStatus] = useState<XeroIntegrationStatus>({
    connected: false,
    tenant_name: null,
    config: null,
    integration_id: null,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);

  const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1`;

  /**
   * Get the current auth headers for edge function calls
   */
  const getAuthHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');
    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    };
  }, []);

  /**
   * Check current Xero integration status
   */
  const checkStatus = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${FUNCTIONS_BASE}/auth-xero`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'status' }),
      });
      const data = await response.json();
      setStatus({
        connected: data.connected || false,
        tenant_name: data.tenant_name || null,
        config: data.config || null,
        integration_id: data.integration_id || null,
      });
    } catch (err) {
      logError('[Xero] Status check failed:', err);
      // Not connected, that's fine
    } finally {
      setLoading(false);
    }
  }, [FUNCTIONS_BASE, getAuthHeaders]);

  /**
   * Start the Xero OAuth authorization flow
   */
  const connect = useCallback(async () => {
    try {
      const redirectUri = `${window.location.origin}/settings/xero/callback`;
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${FUNCTIONS_BASE}/auth-xero?action=authorize&redirect_uri=${encodeURIComponent(redirectUri)}&state=${requireOrganizationId()}`,
        { headers },
      );

      const data = await response.json();
      if (data.url) {
        // Store state for CSRF validation
        sessionStorage.setItem('xero_oauth_state', data.state);
        // Redirect to Xero
        window.location.href = data.url;
      }
    } catch (err: unknown) {
      const message = err instanceof Error
        ? (err.message.includes('Failed to fetch')
          ? 'Xero auth function not deployed. Run: supabase functions deploy auth-xero'
          : err.message)
        : 'Failed to start Xero authorization';
      addToast({ type: 'error', title: 'Connection Failed', message });
    }
  }, [FUNCTIONS_BASE, getAuthHeaders, addToast]);

  /**
   * Handle the OAuth callback (exchange code for tokens)
   */
  const handleCallback = useCallback(async (code: string) => {
    try {
      const redirectUri = `${window.location.origin}/settings/xero/callback`;
      const headers = await getAuthHeaders();

      const response = await fetch(`${FUNCTIONS_BASE}/auth-xero`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'callback',
          code,
          redirect_uri: redirectUri,
        }),
      });

      const data = await response.json();

      if (data.success) {
        addToast({
          type: 'success',
          title: 'Xero Connected',
          message: `Connected to ${data.tenant_name}`,
        });
        await checkStatus();
        return true;
      } else {
        throw new Error(data.error || 'Callback failed');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to complete authorization';
      addToast({ type: 'error', title: 'Connection Failed', message });
      return false;
    }
  }, [FUNCTIONS_BASE, getAuthHeaders, addToast, checkStatus]);

  /**
   * Disconnect from Xero
   */
  const disconnect = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${FUNCTIONS_BASE}/auth-xero`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'disconnect' }),
      });

      const data = await response.json();
      if (data.success) {
        setStatus({
          connected: false,
          tenant_name: null,
          config: null,
          integration_id: null,
        });
        addToast({ type: 'success', title: 'Xero Disconnected' });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to disconnect';
      addToast({ type: 'error', title: 'Disconnect Failed', message });
    }
  }, [FUNCTIONS_BASE, getAuthHeaders, addToast]);

  /**
   * Save project + account mappings
   */
  const saveMappings = useCallback(async (
    projectMappings: Record<string, string>,
    accountMappings: Record<string, string>,
  ) => {
    setSavingMappings(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${FUNCTIONS_BASE}/auth-xero`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'save_mappings',
          project_mappings: projectMappings,
          account_mappings: accountMappings,
        }),
      });

      const data = await response.json();
      if (data.success) {
        addToast({ type: 'success', title: 'Mappings Saved' });
        await checkStatus();
      } else {
        throw new Error(data.error || 'Save failed');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save mappings';
      addToast({ type: 'error', title: 'Save Failed', message });
    } finally {
      setSavingMappings(false);
    }
  }, [FUNCTIONS_BASE, getAuthHeaders, addToast, checkStatus]);

  /**
   * Trigger P&L sync from Xero
   */
  const syncPnl = useCallback(async (months?: number, projectIds?: string[]) => {
    setSyncing(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${FUNCTIONS_BASE}/sync-xero-pnl`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          months: months || 6,
          project_ids: projectIds || null,
        }),
      });

      const data: XeroSyncResult = await response.json();

      if (data.success) {
        addToast({
          type: 'success',
          title: 'Xero Sync Complete',
          message: `${data.summary?.projects_synced} projects, ${data.summary?.total_rows_upserted} rows synced`,
        });
      } else {
        throw new Error(data.error || 'Sync failed');
      }

      return data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      const isReconnect = message.includes('RECONNECT_REQUIRED');
      addToast({
        type: 'error',
        title: isReconnect ? 'Xero Reconnection Required' : 'Sync Failed',
        message: isReconnect
          ? 'Your Xero session has expired. Please disconnect and reconnect your Xero account in Settings → Integrations.'
          : message,
      });
      return { success: false, error: message } as XeroSyncResult;
    } finally {
      setSyncing(false);
    }
  }, [FUNCTIONS_BASE, getAuthHeaders, addToast]);

  /**
   * Clear all synced Xero P&L data for this org (beta feature)
   */
  const [clearing, setClearing] = useState(false);

  const clearXeroData = useCallback(async (projectId?: string) => {
    setClearing(true);
    try {
      const orgId = requireOrganizationId();
      let query = supabase
        .from('claims_pnl_monthly_values')
        .delete()
        .eq('organization_id', orgId)
        .eq('source', 'xero');

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { error, count } = await query;

      if (error) throw error;

      addToast({
        type: 'success',
        title: 'Xero Data Cleared',
        message: projectId
          ? `Removed Xero data for this project`
          : `Removed all Xero-synced P&L data (${count ?? 0} rows)`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to clear data';
      addToast({ type: 'error', title: 'Clear Failed', message });
    } finally {
      setClearing(false);
    }
  }, [addToast]);

  return {
    status,
    loading,
    syncing,
    savingMappings,
    clearing,
    checkStatus,
    connect,
    handleCallback,
    disconnect,
    saveMappings,
    syncPnl,
    clearXeroData,
  };
}
